/**
 * Payment Mapper
 *
 * Transforms backend DTOs (LC_*) to UI models
 */

import { formatToDdMmYyyyHHmmss } from '@/utils';
import type {
  LC_ConceptoPagoItemOut,
  LC_GetConceptosPagoListOut,
  ConceptoPagoItem,
  ConceptosPagoListResult,
} from './payment.types';

/**
 * Replace null or empty string with '-'
 */
const replaceEmpty = (value: string | null | undefined): string => {
  if (!value || value.trim() === '') return '-';
  return value;
};

/**
 * Format a decimal value for display, replacing null with '-'
 */
const formatValue = (value: number | null): string => {
  if (value === null || value === undefined) return '-';
  return value.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Format a date string, replacing null with '-'
 */
const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return '-';
  return formatToDdMmYyyyHHmmss(isoDate);
};

/**
 * Map single payment concept item from backend to UI model
 */
export const mapConceptoPagoItem = (item: LC_ConceptoPagoItemOut): ConceptoPagoItem => ({
  rut: replaceEmpty(item.employeeTaxIdentificationNumber),
  colaborador: replaceEmpty(item.employeeName),
  empresa: replaceEmpty(item.company),
  contrato: String(item.contract),
  concepto: replaceEmpty(item.concept),
  valor: formatValue(item.value),
  generadoEl: formatDate(item.generatedAt),
  valorAnterior: formatValue(item.previousValue),
  fechaEnvio: formatDate(item.sentDate),
  // El backend siempre normaliza status a un texto no vacío (ver ConceptosPagoStatusConstants).
  estado: item.status ?? '-',
});

/**
 * Map full payment concepts list response to UI result
 */
export const mapConceptosPagoListResponse = (
  response: LC_GetConceptosPagoListOut,
): ConceptosPagoListResult => ({
  data: (response.data ?? []).map(mapConceptoPagoItem),
  page: response.pagination.currentPage,
  pageSize: response.pagination.pageSize,
  totalRecords: response.pagination.totalRecords,
  totalPages: response.pagination.totalPages,
});
